import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import StyledLoadingButton from "../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { MITIGATED_RISKS } from "../../../data-hub/components/models/common/events.constants";

interface IFeedbackForm {
  risks: Record<string, boolean>;
}

interface IFeedbackDetailsFormProps {
  mitigatedRisk?: string[];
  submitLoading?: boolean;
  onCancel: () => void;
  onSubmitClick: (mitigatedRisk: string[]) => void;
}

const FeedbackDetailsForm = ({
  mitigatedRisk,
  onCancel,
  onSubmitClick,
  submitLoading,
}: IFeedbackDetailsFormProps) => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<IFeedbackForm>({
    defaultValues: {
      risks: MITIGATED_RISKS.reduce((acc, risk) => {
        const mitigatedRiskFound = mitigatedRisk?.find(r => r === risk);

        acc[risk] = Boolean(mitigatedRiskFound) ?? false;

        return acc;
      }, {} as Record<string, boolean>),
    },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IFeedbackForm> = data => {
    const selectedItems = Object.keys(data.risks).filter(
      key => data.risks[key]
    );

    onSubmitClick(selectedItems);
  };

  const selectedRisks = watch("risks");

  const isAtLeastOneSelected = Object.values(selectedRisks).some(Boolean);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup>
        {MITIGATED_RISKS.map(risk => (
          <Controller
            key={risk}
            name={`risks.${risk}`}
            control={control}
            rules={{
              validate: () =>
                isAtLeastOneSelected || "Please select at least one option.",
            }}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={risk}
              />
            )}
          />
        ))}
      </FormGroup>
      {errors.risks && !isAtLeastOneSelected && (
        <FormHelperText error sx={{ fontSize: "0.75em" }}>
          Please select at least one option.
        </FormHelperText>
      )}
      <Stack
        display="flex"
        flexDirection="row"
        justifyContent="center"
        gap={2}
        sx={{ paddingTop: 3 }}
      >
        <StyledLoadingButton
          startIcon={null}
          loadingPosition="start"
          variant="outlined"
          color="inherit"
          onClick={onCancel}
        >
          Cancel
        </StyledLoadingButton>
        <StyledLoadingButton
          type="submit"
          loading={submitLoading}
          startIcon={null}
          loadingPosition="start"
          variant="contained"
          color="primary"
        >
          Apply
        </StyledLoadingButton>
      </Stack>
    </form>
  );
};

export default FeedbackDetailsForm;
